import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@chakra-ui/react'
import { apiClient } from '../../api'
import { ChatSummarySchema, PaginatedChatSummariesResponse } from '../../types/chatTypes'
import { useLanguage } from '../utility/useLanguage'

export const useDeleteConversation = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useLanguage()

  const removeFromCache = (chatId: string) => {
    const dropFromPage = (page: PaginatedChatSummariesResponse): PaginatedChatSummariesResponse => ({
      ...page,
      items: page.items.filter((chat: ChatSummarySchema) => chat.id !== chatId),
    })

    queryClient.setQueryData(['chatSummaries'], (oldData: any) => {
      if (!oldData?.pages) return oldData
      return { ...oldData, pages: oldData.pages.map(dropFromPage) }
    })

    // Drop it from any active search caches as well.
    queryClient.getQueriesData<any>({ queryKey: ['chatSearch'] }).forEach(([queryKey, data]) => {
      if (!data?.pages) return
      queryClient.setQueryData(queryKey, { ...data, pages: data.pages.map(dropFromPage) })
    })
  }

  const { mutate: deleteConversation, isPending: isDeleting } = useMutation({
    mutationFn: (chatId: string) => apiClient.chatHistory.deleteConversation(chatId),
    onSuccess: (_data, chatId) => {
      removeFromCache(chatId)
    },
    onError: () => {
      toast({
        status: 'error',
        title: t('sidebar.deleteConversationError', { defaultValue: 'Failed to delete conversation' }),
        duration: 4000,
        isClosable: true,
      })
    },
  })

  return { deleteConversation, isDeleting }
}
